import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import * as _ from 'lodash';
import { BaseModal } from '../base-modal';
import { locale as english } from '../i18n/en';
import { HttpService } from 'app/services';

@Component({
    selector: 'app-alert-threshold-modal',
    templateUrl: './alert-threshold-modal.component.html',
    styleUrls: ['./alert-threshold-modal.component.scss'],
})
export class AlertThresholdModalComponent extends BaseModal {
  loading: boolean = true;
  device: any;
  activeForm: FormGroup;
  modalService: any;
  batEnabled: boolean = false;
  states: any;
  initialStateValues: any;

    constructor(
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private httpService: HttpService,
        protected dialogRef: MatDialogRef < AlertThresholdModalComponent > ,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        super(dialogRef, data);       
        this._fuseTranslationLoaderService.loadTranslations(english);
        this.device = data.device;
        this.modalService = data.parentRef;
        
        switch(this.device.type){
          case 'Battery':     
            this.activeForm = new FormGroup({
              alert_threshold: new FormControl({value: (this.device.alert_threshold ? parseFloat(this.device.alert_threshold).toFixed(1) : '11.0'), disabled: !this.device.alert_threshold}, [Validators.min(10.0), Validators.max(12.7)]),
              batEnabled: new FormControl(this.device.alert_threshold ? true : false),
            });
            this.loading = false;
            break;
          case 'THW-PIN':
            this.states = {};
            this.initialStateValues = {};
            this.httpService.getStates(this.device.id).then(res => {
              for(let state of res){
                this.initialStateValues[state.key] = state.changed ? state.change_value : state.value;
                this.states[state.key] = state;
              }
            this.activeForm = new FormGroup({
              min_humidity: new FormControl({value: this.initialStateValues.min_humidity || 50, disabled: !this.initialStateValues.humidity_enabled}, [Validators.min(1)]),
              max_humidity: new FormControl({value: this.initialStateValues.max_humidity || 80, disabled: !this.initialStateValues.humidity_enabled}, [Validators.max(99)]),
              humidity_enabled: new FormControl(this.initialStateValues.humidity_enabled),
              min_temp: new FormControl({value: this.initialStateValues.min_temp || 39, disabled: !this.initialStateValues.temp_enabled}, [Validators.min(-40)]),
              max_temp: new FormControl({value: this.initialStateValues.max_temp || 58, disabled: !this.initialStateValues.temp_enabled}, [Validators.max(122)]),
              temp_enabled: new FormControl(this.initialStateValues.temp_enabled),
            });
            this.loading = false;
            }).catch(err => console.error(err));

            break;
        }
    }


    validateRange($event){
      let type = $event.target.getAttribute('formcontrolname').split('_')[1],
          diff = type == 'temp' ? 18 : 10,
          minKey = `min_${type}`,
          maxKey = `max_${type}`,
          minVal = this.activeForm.value[minKey],
          maxVal = this.activeForm.value[maxKey],
          minInput = this.activeForm.get(minKey),
          maxInput = this.activeForm.get(maxKey),
          minErrs = false,
          maxErrs = false;

      let [min, max] = type == 'temp' ?
          [-40, 122] :
          [1, 99];

      let errs: any = {};

      if((maxVal - minVal) < diff){
        minErrs = maxErrs = true;
        if((maxVal - minVal) < 0)
          errs.noncompliant = true;
        else
          errs.encroaching = true;
      }

      if(minVal < min){
        errs.min = true;
        minErrs = true;
      }

      if(maxVal > max){
        errs.max = true;
        maxErrs = true;
      }

      if(Object.keys(errs).length < 1)
        errs = null;

        this.activeForm.markAllAsTouched();
        minInput.markAsDirty();
        minInput.markAsTouched();
        maxInput.markAsDirty();
        maxInput.markAsTouched();
        minInput.setErrors(errs);
        maxInput.setErrors(errs);

        //you arent supposed to do this:
        if(minErrs)
          (minInput as any).status = 'INVALID';

        if(maxErrs)
          (maxInput as any).status = 'INVALID';


    }
    toggleHumidity(){
      ['min_humidity', 'max_humidity'].forEach(el => 
        this.activeForm.get(el)[this.activeForm.get('humidity_enabled').value ? 'enable' : 'disable']()
      );
    }

    toggleTemp(){
      ['min_temp', 'max_temp'].forEach(el => 
        this.activeForm.get(el)[this.activeForm.get('temp_enabled').value ? 'enable' : 'disable']()
      );
    }

    toggleBat(){
      this.activeForm.get('alert_threshold')[this.activeForm.get('batEnabled').value ? 'enable' : 'disable']();
    }

    save(){
      let formValues = this.activeForm.value;
      let errorFlag = false;

      if(this.device.type == 'Battery'){
        if(formValues.batEnabled && (this.device.alert_threshold == formValues.alert_threshold))
          return;
        if(!formValues.batEnabled && !this.device.alert_threshold)
          return;

        let body = {alert_threshold: formValues.batEnabled ? parseFloat(formValues.alert_threshold) : null};
        this.httpService.patchAmbiguousDevice(this.device, body).toPromise().then((res: any) => {
          if(res.count == 1){
            this.device.alert_threshold = body.alert_threshold;
            this.dialogRef.close();
          }
          throw `Failed to save changes to device ${this.device.serial}`;
        }).catch(err => {
          this.modalService.errorDialog(err);
        });
      } else {
        let updatesRequired = [];

        let evaluateRangeVals = (sfx: 'humidity' | 'temp') => { //no need to evaluate threshold values unless sensor notifications are enabled
          for(let key of ['max', 'min'].map(pfx => `${pfx}_${sfx}`))
            if(this.initialStateValues[key] !== formValues[key]){
              //need to create or update state for min/max sensor notification threshold
              updatesRequired.push(key);
            }
        };

        let evaluateToggleVals = (pfx: 'humidity' | 'temp') => {
          let key = `${pfx}_enabled`;
          if(formValues[key]) //as long as sensor notifications are enabled, need to make sure no updates are necessary for relevant threshold values
            evaluateRangeVals(pfx);
          if(this.initialStateValues[key] !== formValues[key]){ 
            //need to create or update state for sensor notification enabled status
            updatesRequired.push(key);
          }
        };

        //evaluate whether to update notification enabled states, and associated threshold states accordingly
        ['humidity', 'temp'].forEach((pfx: any)=> evaluateToggleVals(pfx));
        let httpRequests = updatesRequired.map(key => {
          let body: any = {
              key: key, 
              change_value: formValues[key], 
              changed: true
            };
          if(this.states[key]?.id)
            body.id = parseInt(this.states[key].id);

          return this.httpService[`${body.id ? 'patch' : 'post'}State`](this.device.id, body).then(() => {
            return true;
          }).catch(err => {
            this.modalService.errorDialog(err);
            errorFlag = true;
          });
        });

        Promise.all(httpRequests).then(res => errorFlag ?
          this.modalService.showModal('Error', 'Failed to save one or more changes made. Please try again.') :
          this.dialogRef.close()
        ).catch(err => this.modalService.errorDialog('Error', err));
      }
    }
}